import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Modal, Button } from "react-bootstrap";
import Moment from "moment";
import { useLocation, useNavigate } from "react-router-dom";
import { apiHandler } from "../../../api/apihandler";
import {
  DAILY_REPORTS_LIST,
  NOTIFICATION_LIST,
  NOTIFICATION_READ,
  TEACHERS_GET_ASSIGNED_GAMES_API,
} from "../../../api/constants";
import Announcement from "../../../components/announcement";
import { getEffectiveStudentId, isParentLoginProfile, getSelectedChildProfile, getParentChildren, getStoredParentStudentId, setStoredParentStudentId } from "../../../utilities/teacherModulePermissions";
import { setUserData } from "../../../store/setting/actions";
import "../../../assets/css/student-dashboard.css";

function stripHtml(value) {
  if (!value) return "";
  return String(value).replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

// report_date comes back from the API as a full ISO instant (e.g.
// "2026-09-18T18:30:00.000+05:30") carrying the API server's own timezone
// offset, even though it's meant to represent a plain calendar date - which
// daycare day this report is for, not a moment in time. Moment(reportDate)
// re-interprets that instant into the VIEWER'S browser-local timezone before
// any day-level comparison happens, which silently shifts the date backward
// a day for anyone far enough behind the server's offset (US/Canada viewing
// an India-hosted server, for example) - "Today" renders as "Yesterday" for
// them while it's correct for a viewer whose offset happens to be close to
// the server's. Reading only the leading YYYY-MM-DD digits and parsing that
// as a plain local date sidesteps the conversion entirely, so every viewer
// sees the same calendar date the server actually meant, regardless of their
// own timezone.
function parseReportDate(reportDate) {
  const dateOnly = String(reportDate || "").slice(0, 10);
  return Moment(dateOnly, "YYYY-MM-DD", true);
}

function parseMinutes(value) {
  if (!value) return 0;
  const parsed = Moment(value, ["HH:mm:ss", "HH:mm"], true);
  if (!parsed.isValid()) return 0;
  return parsed.hours() * 60 + parsed.minutes();
}

function formatTime(value) {
  if (!value) return "--";
  const parsed = Moment(value, ["HH:mm:ss", "HH:mm", Moment.ISO_8601], true);
  if (!parsed.isValid()) return "--";
  return parsed.format("h:mm A");
}

function sleepHours(report) {
  const sleeps = Array.isArray(report?.daily_report_sleeps) ? report.daily_report_sleeps : [];
  const total = sleeps.reduce((sum, item) => {
    const start = parseMinutes(item?.start_time);
    const end = parseMinutes(item?.end_time);
    if (!start || !end || end <= start) return sum;
    return sum + (end - start);
  }, 0);
  return `${(total / 60).toFixed(1)}h`;
}

function getApiList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload?.data)) return payload.data;
  if (Array.isArray(payload?.data?.data)) return payload.data.data;
  return [];
}

function getNotificationId(item) {
  return item?.id || item?.notification_id || null;
}

function getMealAmountLabel(value) {
  if (typeof value === "string" && value.trim()) return value.trim();
  const map = {
    0: "None",
    1: "Some",
    2: "Half",
    3: "Most",
    4: "All",
    5: "All+",
  };
  return map[Number(value)] || "Some";
}

function getMealAmountPercent(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    const labelMap = {
      none: 0,
      some: 25,
      half: 50,
      most: 75,
      all: 90,
      "all+": 100,
    };

    if (Object.prototype.hasOwnProperty.call(labelMap, normalized)) {
      return labelMap[normalized];
    }
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (numeric >= 0 && numeric <= 5) return Math.min(100, numeric * 20);
  return Math.max(0, Math.min(100, numeric));
}

function getMealAmountStep(value) {
  if (value === null || value === undefined || value === "") return 0;

  if (typeof value === "string") {
    const normalized = value.trim().toLowerCase();
    const labelMap = {
      none: 0,
      some: 1,
      half: 2,
      most: 3,
      all: 4,
      "all+": 5,
    };

    if (Object.prototype.hasOwnProperty.call(labelMap, normalized)) {
      return labelMap[normalized];
    }
  }

  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return 0;
  if (numeric >= 0 && numeric <= 5) return numeric;
  return Math.max(0, Math.min(5, Math.round(numeric / 20)));
}

const MEAL_PROGRESS_LABELS = ["None", "Some", "Half", "Most", "All", "All+"];

function flattenDailyReportMeals(groups = []) {
  if (!Array.isArray(groups)) return [];

  return groups.flatMap((group) => {
    if (Array.isArray(group?.items)) {
      const mealTypeName = group?.menu_type || group?.meal_type || group?.meal_type_name || "Meal";
      return group.items.map((item) => ({
        ...item,
        meal_type_name: item?.meal_type_name || mealTypeName,
      }));
    }

    return [group];
  });
}

function getProfileMetaLine(profile) {
  const parts = [];
  const level =
    profile?.class_student?.class?.class_name ||
    profile?.class_student?.class?.name ||
    profile?.user_setting?.education_level;
  const room = profile?.user_teachers_and_room?.room?.room_name || profile?.class_student?.room?.room_name;
  const school = profile?.organization?.organization_name || profile?.organization_name || profile?.school_name;
  if (level) parts.push(level);
  if (room) parts.push(room);
  if (school) parts.push(school);
  return parts.length ? parts.join(" · ") : "Student care log";
}

function moodColor(text) {
  const value = String(text || "").toLowerCase();
  if (value.includes("happy") || value.includes("calm") || value.includes("settled")) return "#1D9E75";
  if (value.includes("quiet") || value.includes("sad") || value.includes("upset")) return "#378ADD";
  return "#888";
}

function diaperTag(check) {
  if (Number(check) === 3) return { label: "Wet", className: "dcl-tag-wet" };
  if (Number(check) === 4) return { label: "BM", className: "dcl-tag-bm" };
  return { label: "Dry", className: "dcl-tag-dry" };
}

const sectionList = ["mood", "sleep", "eating", "diaper", "incident", "medication", "note", "games", "completedGames"];

function StudentDashboard() {
  const rawProfile = useSelector((state) => state.setting.setting.user_data);
  const profileData = useMemo(() => {
    if (!rawProfile) return {};
    if (typeof rawProfile === "object") return rawProfile;
    try {
      return JSON.parse(rawProfile);
    } catch (error) {
      return {};
    }
  }, [rawProfile]);

  const studentId = useMemo(() => getEffectiveStudentId(profileData), [profileData]);
  const dispatch = useDispatch();
  // A parent with more than one linked child lands here without a selection
  // made yet (sign-in.js/login-with-otp.js only auto-pick when there's exactly
  // one child) — block the dashboard behind a "select a child" prompt until
  // they pick one. Changing child later happens from the profile screen.
  const parentChildren = getParentChildren(profileData);
  const needsChildSelection = isParentLoginProfile(profileData) && parentChildren.length > 1 && !getStoredParentStudentId();

  function handleSelectChild(childId) {
    setStoredParentStudentId(childId);
    dispatch(setUserData(JSON.stringify({ ...profileData, selected_student_id: childId })));
  }

  const navigate = useNavigate();
  const location = useLocation();

  const targetReportId = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const id = params.get("reportId");
    return id ? Number(id) : null;
  }, [location.search]);

  const [dailyReports, setDailyReports] = useState([]);
  const [notifications, setNotifications] = useState([]);
  const [assignedGames, setAssignedGames] = useState([]);
  const [completedGames, setCompletedGames] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAcknowledging, setIsAcknowledging] = useState(false);
  const [selectedReportIndex, setSelectedReportIndex] = useState(0);
  const [showHistoricalReport, setShowHistoricalReport] = useState(false);
  const [openSections, setOpenSections] = useState(() => {
    const state = {};
    sectionList.forEach((key) => {
      state[key] = true;
    });
    return state;
  });

  function openGame(game) {
    if (!game) {
      navigate("/dashboard/app/game/game-category");
      return;
    }

    const gameNameId = game?.game_name_id || game?.game_name?.id;
    const categoryId = game?.game_name?.category?.id;

    if (!gameNameId || !categoryId) {
      navigate("/dashboard/app/game/game-category");
      return;
    }

    navigate(`/dashboard/app/game/game-category?gameNameId=${gameNameId}&categoryId=${categoryId}&autoPlay=1`);
  }

  async function loadDashboardData() {
    setIsLoading(true);
    try {
      const reportPayload = { page: 1, limit: 30 };
      const notificationPayload = { page: 1, limit: 30 };
      if (studentId) {
        reportPayload.student_id = studentId;
        notificationPayload.student_id = studentId;
      }

      const [reportRes, notificationRes] = await Promise.all([
        apiHandler(DAILY_REPORTS_LIST, "POST", reportPayload),
        apiHandler(NOTIFICATION_LIST, "POST", notificationPayload),
      ]);

      if (reportRes?.data?.response?.status === 200) {
        const reports = getApiList(reportRes?.data?.data?.data);
        const completedGamesList = Array.isArray(reportRes?.data?.data?.completed_games)
          ? reportRes.data.data.completed_games
          : [];
        const sorted = [...reports].sort((a, b) => {
          const left = parseReportDate(a?.report_date);
          const right = parseReportDate(b?.report_date);
          if (!left.isValid() && !right.isValid()) return 0;
          if (!left.isValid()) return 1;
          if (!right.isValid()) return -1;
          return right.valueOf() - left.valueOf();
        });

        const todayReportIndex = sorted.findIndex((report) => (
          report?.report_date && parseReportDate(report.report_date).isValid() && parseReportDate(report.report_date).isSame(Moment(), "day")
        ));

        setDailyReports(sorted);
        if (targetReportId) {
          const idx = sorted.findIndex((r) => Number(r?.id) === targetReportId);
          setSelectedReportIndex(idx >= 0 ? idx : 0);
          setShowHistoricalReport(true);
        } else if (todayReportIndex >= 0) {
          setSelectedReportIndex(todayReportIndex);
          setShowHistoricalReport(true);
        } else {
          setSelectedReportIndex(0);
          setShowHistoricalReport(false);
        }
        setCompletedGames(completedGamesList);
      } else {
        setDailyReports([]);
        setCompletedGames([]);
        setShowHistoricalReport(false);
      }

      if (notificationRes?.data?.response?.status === 200) {
        setNotifications(getApiList(notificationRes?.data?.data?.notifications));
      } else {
        setNotifications([]);
      }
    } catch (error) {
      console.error(error);
      setDailyReports([]);
      setNotifications([]);
      setCompletedGames([]);
      setShowHistoricalReport(false);
    } finally {
      setIsLoading(false);
    }
  }

  async function loadAssignedGames() {
    if (!studentId) return;
    try {
      const response = await apiHandler(TEACHERS_GET_ASSIGNED_GAMES_API, "POST", {
        student_id: studentId,
        user_id: studentId,
      });
      if (response?.data?.response?.status === 200) {
        setAssignedGames(response?.data?.data?.assigned_games || []);
      } else {
        setAssignedGames([]);
      }
    } catch (error) {
      console.error(error);
      setAssignedGames([]);
    }
  }

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: "instant" });
    if (localStorage.getItem("uzoneAuthToken") && !needsChildSelection) {
      loadDashboardData();
      loadAssignedGames();
    }
  }, [studentId, needsChildSelection]);

  const latestReport = showHistoricalReport ? dailyReports[selectedReportIndex] || null : null;
  const hasDailyReport = Boolean(latestReport);
  const hasAnyHistoricalReport = dailyReports.length > 0;
  const hasTodayReport = dailyReports.some((report) => (
    report?.report_date && parseReportDate(report.report_date).isValid() && parseReportDate(report.report_date).isSame(Moment(), "day")
  ));
  const shouldShowMissingTodayState = !isLoading && !hasDailyReport && hasAnyHistoricalReport && !showHistoricalReport && !targetReportId;
  const shouldShowDateNavigation = showHistoricalReport && hasAnyHistoricalReport;
  // A parent viewing this dashboard should see the selected child's name/room,
  // not their own — the parent's own record has no class/room fields at all.
  const displayProfile = isParentLoginProfile(profileData) ? (getSelectedChildProfile(profileData) || {}) : profileData;
  const studentName = `${displayProfile?.first_name || "Student"} ${displayProfile?.last_name || ""}`.trim();
  const metaLine = getProfileMetaLine(displayProfile);

  const reportDate = latestReport?.report_date && parseReportDate(latestReport.report_date).isValid()
    ? parseReportDate(latestReport.report_date)
    : Moment();

  const reportDayDiff = hasDailyReport ? Moment().startOf("day").diff(reportDate.clone().startOf("day"), "days") : null;
  const dateLabel = hasDailyReport
    ? reportDayDiff === 0
      ? `Today, ${reportDate.format("MMM D")}`
      : reportDayDiff === 1
        ? `Yesterday, ${reportDate.format("MMM D")}`
        : reportDate.format("ddd, MMM D")
    : shouldShowMissingTodayState
      ? `Today, ${Moment().format("MMM D")}`
      : "No daily report";

  const canGoPrevious = showHistoricalReport && selectedReportIndex < dailyReports.length - 1;
  const canGoNext = showHistoricalReport && selectedReportIndex > 0;

  const moods = (latestReport?.daily_report_fealings || [])
    .map((item) => item?.fealing?.name)
    .filter(Boolean)
    .slice(0, 5);

  const mealGroups = Array.isArray(latestReport?.daily_report_meals) ? latestReport.daily_report_meals : [];
  const meals = flattenDailyReportMeals(mealGroups);
  const sleeps = Array.isArray(latestReport?.daily_report_sleeps) ? latestReport.daily_report_sleeps : [];
  const diapers = Array.isArray(latestReport?.daily_report_diapers) ? latestReport.daily_report_diapers : [];

  const unreadNotifications = notifications.filter((item) => Number(item?.is_read) !== 1);
  // Notification objects carry their text in `description` (see notification.js,
  // the notification bell page, which reads elm?.description) - this used to
  // filter/read `.message`, a field these objects don't have, so both alert
  // lists were always empty and this section only ever rendered its "nothing
  // to show" fallback, yet still let you click Acknowledge on nothing.
  const incidentAlerts = unreadNotifications.filter((item) => /incident|injury|accident|urgent|allergy/i.test(String(item?.description || "")));
  const medicationAlerts = unreadNotifications.filter((item) => /medication|medicine|dose/i.test(String(item?.description || "")));

  const sleepTimeline = sleeps.map((entry) => {
    const start = formatTime(entry?.start_time);
    const end = formatTime(entry?.end_time);
    const startMin = parseMinutes(entry?.start_time);
    const endMin = parseMinutes(entry?.end_time);
    const mins = startMin && endMin && endMin > startMin ? endMin - startMin : 0;
    return {
      time: start,
      title: `Rest period - ${(mins / 60).toFixed(2)} h`,
      sub: `${start} to ${end}`,
      note: stripHtml(entry?.notes) || "Slept and woke with classroom support.",
      orderMinutes: startMin,
    };
  }).sort((a, b) => a.orderMinutes - b.orderMinutes);

  const mealCards = mealGroups.map((group, index) => {
    const groupLabel = group?.menu_type || group?.meal_type || group?.meal_type_name || `Meal ${index + 1}`;
    const items = Array.isArray(group?.items) ? group.items : [];
    const normalizedItems = items.map((item, itemIndex) => {
      const mealName = stripHtml(item?.menu_name || item?.meal_name || item?.name || `Meal item ${itemIndex + 1}`) || `Meal item ${itemIndex + 1}`;
      return {
        id: item?.id || item?.menu_meal_id || `${groupLabel}-${itemIndex}`,
        name: mealName,
        time: formatTime(item?.meal_time),
        amountLabel: getMealAmountLabel(item?.meal_amount),
        amountStep: getMealAmountStep(item?.meal_amount),
        percent: getMealAmountPercent(item?.meal_amount),
      };
    });

    return {
      label: groupLabel,
      count: normalizedItems.length,
      eatenLabel: normalizedItems.length ? `${normalizedItems.length} item${normalizedItems.length !== 1 ? "s" : ""} logged` : "Not logged",
      items: normalizedItems,
    };
  });

  const summaryMood = moods[0] || "Calm";
  const recentCompletedGames = completedGames.slice(0, 8);

  function toggleSection(key) {
    setOpenSections((prev) => ({ ...prev, [key]: !prev[key] }));
  }

  async function acknowledgeIncident() {
    const incident = incidentAlerts[0];
    const id = getNotificationId(incident);
    if (isAcknowledging) return;

    setIsAcknowledging(true);
    try {
      if (id) {
        const wasUnread = incident?.is_read !== 1;
        const response = await apiHandler(NOTIFICATION_READ, "POST", { notification_id: id });
        if (response?.data?.response?.status === 200) {
          setNotifications((prev) =>
            prev.map((item) => (getNotificationId(item) === id ? { ...item, is_read: 1 } : item))
          );
          window.dispatchEvent(new CustomEvent('notification-count-refresh', wasUnread ? { detail: { delta: -1 } } : undefined));
        }
      }

      navigate("/dashboard/accident-reports");
    } catch (error) {
      console.error(error);
      navigate("/dashboard/accident-reports");
    } finally {
      setIsAcknowledging(false);
    }
  }

  return (
    <>
      <Modal show={needsChildSelection} backdrop="static" keyboard={false} centered>
        <Modal.Header>
          <Modal.Title style={{ fontSize: '18px' }}>Select a Child</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <p style={{ color: '#6b7280', fontSize: '14px' }}>
            You have more than one child linked to your account. Select which child's dashboard you'd like to view — you can change this any time from your profile.
          </p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
            {parentChildren.map((child) => (
              <Button
                key={child?.id}
                variant="outline-primary"
                className="d-flex align-items-center"
                style={{ gap: '10px', padding: '10px 14px', textAlign: 'left' }}
                onClick={() => handleSelectChild(child?.id)}
              >
                <img
                  src={child?.user_setting?.photo || 'https://uzone360live.s3.amazonaws.com/noimage.png'}
                  alt=""
                  style={{ width: '32px', height: '32px', borderRadius: '50%', objectFit: 'cover' }}
                />
                <span>{child?.first_name} {child?.last_name}</span>
              </Button>
            ))}
          </div>
        </Modal.Body>
      </Modal>
      <div id="content-page" className="content-page dcl-page">
      <Announcement maxWidth="740px" outerPadding="12px 0 6px" showAuthorMeta={false} showCloseButton={true} />
      <div className="dcl-wrap">
        <div className="dcl-top-bar">
          <div className="dcl-child-avi">
            {displayProfile?.user_setting?.photo ? (
              <img
                src={displayProfile.user_setting.photo}
                alt=""
                style={{ width: "100%", height: "100%", borderRadius: "50%", objectFit: "cover" }}
              />
            ) : (
              studentName.split(" ").map((part) => part?.[0] || "").join("").slice(0, 2).toUpperCase()
            )}
          </div>
          <div>
            <div className="dcl-child-name">{studentName}</div>
            <div className="dcl-child-meta">{hasDailyReport ? metaLine : `${metaLine} · Daily report pending`}</div>
          </div>
          {shouldShowDateNavigation ? (
            <div className="dcl-date-nav">
              <button className="dcl-date-btn" onClick={() => canGoPrevious && setSelectedReportIndex((prev) => prev + 1)} disabled={!canGoPrevious || isLoading}>‹</button>
              <div className="dcl-date-lbl">{dateLabel}</div>
              <button className="dcl-date-btn" onClick={() => canGoNext && setSelectedReportIndex((prev) => prev - 1)} disabled={!canGoNext || isLoading}>›</button>
            </div>
          ) : null}
        </div>

        {hasDailyReport ? (
          <>
            <div className="dcl-alert-banner">
              <div className="dcl-alert-icon">Rx</div>
              <div>
                <div className="dcl-alert-title">Medication administered today</div>
                <div className="dcl-alert-body">
                  {stripHtml(medicationAlerts[0]?.description) || "No medication update was logged today."}
                </div>
              </div>
            </div>

            <div className="dcl-summary-grid">
              <div className="dcl-sum-card"><div className="dcl-sum-val">{sleepHours(latestReport)}</div><div className="dcl-sum-lbl">Total nap time</div></div>
              <div className="dcl-sum-card"><div className="dcl-sum-val">{meals.length}</div><div className="dcl-sum-lbl">Meals and snacks</div></div>
              <div className="dcl-sum-card"><div className="dcl-sum-val">{diapers.length}</div><div className="dcl-sum-lbl">Nappy checks</div></div>
              <div className="dcl-sum-card"><div className="dcl-sum-val dcl-mood-green">{summaryMood}</div><div className="dcl-sum-lbl">Overall mood</div></div>
            </div>

            <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("mood")}>
            <div className="dcl-section-icon dcl-bg-amber">🙂</div>
            <div className="dcl-section-title">Mood and behaviour</div>
            <span className="dcl-section-badge dcl-bg-amber">{summaryMood} day</span>
            <span className={`dcl-chevron ${openSections.mood ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.mood ? (
            <div className="dcl-section-body">
              <div className="dcl-mood-row">
                {(moods.length ? moods : ["Calm and settled"]).map((mood, idx) => (
                  <div className="dcl-mood-chip" key={`mood-${idx}`}>
                    <div className="dcl-mood-chip-dot" style={{ background: moodColor(mood) }}></div>
                    {mood}
                  </div>
                ))}
              </div>
              {stripHtml(latestReport?.observation) ? (
                <div className="dcl-note-box">
                  <div className="dcl-note-text">{stripHtml(latestReport?.observation)}</div>
                  <div className="dcl-note-sig">Educator note · {reportDate.format("h:mm A")}</div>
                </div>
              ) : (
                <div className="dcl-note-box">
                  <div className="dcl-note-text dcl-note-empty">No observation note recorded for this report.</div>
                </div>
              )}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("sleep")}>
            <div className="dcl-section-icon dcl-bg-blue">💤</div>
            <div className="dcl-section-title">Sleep and rest</div>
            <span className="dcl-section-badge dcl-bg-blue">{sleeps.length} naps · {latestReport ? sleepHours(latestReport) : "0.0h"}</span>
            <span className={`dcl-chevron ${openSections.sleep ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.sleep ? (
            <div className="dcl-section-body">
              <div className="dcl-timeline">
                <div className="dcl-tl-line"></div>
                {(sleepTimeline.length ? sleepTimeline : [{ time: "--", title: "No sleep logs", sub: "No entries today", note: "" }]).map((entry, idx) => (
                  <div className="dcl-tl-item" key={`sleep-${idx}`}>
                    <div className="dcl-tl-dot dcl-tl-blue"></div>
                    <div className="dcl-tl-time">{entry.time}</div>
                    <div>
                      <div className="dcl-tl-title">{entry.title}</div>
                      <div className="dcl-tl-sub">{entry.sub}</div>
                      {entry.note ? <div className="dcl-tl-note">{entry.note}</div> : null}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("eating")}>
            <div className="dcl-section-icon dcl-bg-green">🍽</div>
            <div className="dcl-section-title">Eating and nutrition</div>
            <span className="dcl-section-badge dcl-bg-green">{meals.length ? `${meals.length} items logged` : "No entries"}</span>
            <span className={`dcl-chevron ${openSections.eating ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.eating ? (
            <div className="dcl-section-body">
              <div className="dcl-meal-grid dcl-meal-grid-nutrition">
                {mealCards.map((meal, idx) => (
                  <div className="dcl-meal-card dcl-meal-card-rich" key={`meal-${idx}`}>
                    <div className="dcl-meal-card-top">
                      <div className="dcl-meal-label">{meal.label}</div>
                      <div className="dcl-meal-count">{meal.count} item{meal.count !== 1 ? "s" : ""}</div>
                    </div>
                    <div className="dcl-eaten-lbl">{meal.eatenLabel}</div>
                    <div className="dcl-meal-item-list">
                      {meal.items.length ? meal.items.map((mealItem) => (
                        <div className="dcl-meal-item" key={mealItem.id}>
                          <div className="dcl-meal-item-copy">
                            <div className="dcl-meal-item-name">{mealItem.name}</div>
                            <div className="dcl-meal-item-meta">{mealItem.time}</div>
                            <div className="dcl-meal-progress-wrap">
                              <input
                                type="range"
                                min={0}
                                max={5}
                                step={1}
                                value={mealItem.amountStep}
                                readOnly
                                className="dcl-meal-progress-input"
                              />
                              <div className="dcl-meal-progress-scale">
                                {MEAL_PROGRESS_LABELS.map((label) => (
                                  <span key={`${mealItem.id}-${label}`}>{label}</span>
                                ))}
                              </div>
                            </div>
                          </div>
                          <div className="dcl-meal-item-amount">{mealItem.amountLabel}</div>
                        </div>
                      )) : (
                        <div className="dcl-meal-item dcl-meal-item-empty">
                          <div className="dcl-meal-item-name">No meal items recorded</div>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
              </div>
              <div className="dcl-note-box dcl-mt-14">
                <div className="dcl-note-text">
                  {stripHtml(latestReport?.learned) || "Meal participation and appetite were tracked by the educator during the day."}
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("diaper")}>
            <div className="dcl-section-icon dcl-bg-amber">🚼</div>
            <div className="dcl-section-title">Nappy and toilet</div>
            <span className="dcl-section-badge dcl-bg-amber">{diapers.length} checks</span>
            <span className={`dcl-chevron ${openSections.diaper ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.diaper ? (
            <div className="dcl-section-body">
              {(diapers.length ? diapers : [{ diaper_time: null, diaper_check: null }]).map((entry, idx) => {
                const tag = diaperTag(entry?.diaper_check);
                return (
                  <div className="dcl-diaper-row" key={`diaper-${idx}`}>
                    <div className="dcl-diaper-time">{formatTime(entry?.diaper_time)}</div>
                    <div className="dcl-diaper-type">Routine check</div>
                    <span className={`dcl-diaper-tag ${tag.className}`}>{tag.label}</span>
                  </div>
                );
              })}
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("medication")}>
            <div className="dcl-section-icon dcl-bg-blue">💊</div>
            <div className="dcl-section-title">Medication</div>
            <span className="dcl-section-badge dcl-bg-green">{medicationAlerts.length} dose logged</span>
            <span className={`dcl-chevron ${openSections.medication ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.medication ? (
            <div className="dcl-section-body">
              <div className="dcl-med-box">
                <div className="dcl-med-header">
                  <div className="dcl-med-name">Medication update</div>
                  <span className="dcl-med-status">Given</span>
                </div>
                <div className="dcl-med-details">
                  <div className="dcl-med-detail"><strong>Date:</strong> {reportDate.format("MMM D, YYYY")}</div>
                  <div className="dcl-med-detail"><strong>Time:</strong> {medicationAlerts[0]?.created_at ? Moment(medicationAlerts[0].created_at).format("h:mm A") : "--"}</div>
                  <div className="dcl-med-detail"><strong>Source:</strong> Notification</div>
                  <div className="dcl-med-detail"><strong>Status:</strong> Recorded</div>
                </div>
                <div className="dcl-note-box">
                  <div className="dcl-note-text">{stripHtml(medicationAlerts[0]?.description) || "No medication detail was posted for this report."}</div>
                  <div className="dcl-note-sig">Recorded by educator</div>
                </div>
              </div>
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("note")}>
            <div className="dcl-section-icon dcl-bg-purple">📝</div>
            <div className="dcl-section-title">End-of-day note from educator</div>
            <span className={`dcl-chevron ${openSections.note ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.note ? (
            <div className="dcl-section-body">
              <div className="dcl-note-box">
                <div className="dcl-note-text">{stripHtml(latestReport?.comments) || "No end-of-day note posted yet."}</div>
                <div className="dcl-note-sig">Educator · {reportDate.format("h:mm A")}</div>
              </div>
            </div>
          ) : null}
        </section>
          </>
        ) : shouldShowMissingTodayState ? (
          <section className="dcl-empty-state">
            <div className="dcl-empty-icon">📅</div>
            <div className="dcl-empty-title">Today&apos;s daily report is not available</div>
            <div className="dcl-empty-text">
              No daily report has been submitted for today yet. You can view a report from another day,
              or refresh this page later once today&apos;s report is published.
            </div>
            <div className="dcl-empty-actions">
              <button
                className="dcl-empty-btn dcl-empty-btn-primary"
                onClick={() => {
                  setSelectedReportIndex(0);
                  setShowHistoricalReport(true);
                }}
                disabled={isLoading || !hasAnyHistoricalReport || hasTodayReport}
              >
                View Past Reports
              </button>
              <button className="dcl-empty-btn" onClick={loadDashboardData} disabled={isLoading}>
                {isLoading ? "Refreshing..." : "Refresh report"}
              </button>
            </div>
          </section>
        ) : (
          <section className="dcl-empty-state">
            <div className="dcl-empty-icon">🗒</div>
            <div className="dcl-empty-title">Daily report not available yet</div>
            <div className="dcl-empty-text">
              No daily care log has been submitted for {studentName} yet. Once the educator records today&apos;s activities,
              meals, sleep, and notes, the full report will appear here.
            </div>
            <div className="dcl-empty-actions">
              <button className="dcl-empty-btn dcl-empty-btn-primary" onClick={loadDashboardData} disabled={isLoading}>
                {isLoading ? "Refreshing..." : "Refresh report"}
              </button>
              <button className="dcl-empty-btn" onClick={() => navigate("/dashboard/app/game/game-category")}>
                Browse games
              </button>
            </div>
          </section>
        )}

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("incident")}>
            <div className="dcl-section-icon dcl-bg-amber">⚠</div>
            <div className="dcl-section-title">Incidents and accidents</div>
            <span className="dcl-section-badge dcl-bg-amber">{incidentAlerts.length} incident</span>
            <span className={`dcl-chevron ${openSections.incident ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.incident ? (
            <>
              <div className="dcl-section-body">
                <div className="dcl-incident-box">
                  <div className="dcl-inc-header">
                    <div className="dcl-inc-dot"></div>
                    <div className="dcl-inc-title">Incident update</div>
                    <div className="dcl-inc-time">{incidentAlerts[0]?.created_at ? Moment(incidentAlerts[0].created_at).format("h:mm A") : "--"}</div>
                  </div>
                  <div className="dcl-inc-body">
                    {stripHtml(incidentAlerts[0]?.description) || "No active incident alert is available right now. You can still open the accident reports screen to review submitted records."}
                  </div>
                  <div className="dcl-inc-sig">Classroom safety record</div>
                </div>
              </div>
              {/* Nothing to acknowledge unless there's an actual unread
                  incident-shaped notification — this row used to render
                  unconditionally, so "Acknowledge" kept showing (and stayed
                  clickable) even with zero incidents, doing nothing but
                  navigate away each time it was clicked. */}
              {incidentAlerts.length > 0 && (
                <div className="dcl-sign-row">
                  <div className="dcl-sign-lbl">
                    Please acknowledge you have read this incident report
                  </div>
                  <button className="dcl-sign-btn" onClick={acknowledgeIncident} disabled={isAcknowledging}>
                    {isAcknowledging ? "Saving..." : "Acknowledge"}
                  </button>
                </div>
              )}
            </>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("games")}>
            <div className="dcl-section-icon dcl-bg-green">🎮</div>
            <div className="dcl-section-title">Assigned games</div>
            <span className="dcl-section-badge dcl-bg-green">{assignedGames.length} assigned</span>
            <span className={`dcl-chevron ${openSections.games ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.games ? (
            <div className="dcl-section-body">
              <div className="dcl-games-grid">
                {(assignedGames.length ? assignedGames : [null]).map((game, idx) => (
                  <div className="dcl-game-card" key={`game-${idx}`}>
                    <div className="dcl-game-title">{game?.game_name?.name || "No game assigned"}</div>
                    <div className="dcl-game-meta">{game?.game_name?.category?.name || "Check back later"}</div>
                    <button
                      className="dcl-game-btn"
                      onClick={() => openGame(game)}
                    >
                      {game ? "Play" : "Browse"}
                    </button>
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>

        <section className="dcl-section">
          <div className="dcl-section-header" onClick={() => toggleSection("completedGames")}>
            <div className="dcl-section-icon dcl-bg-blue">🏆</div>
            <div className="dcl-section-title">Completed games</div>
            <span className="dcl-section-badge dcl-bg-blue">{completedGames.length} completed</span>
            <span className={`dcl-chevron ${openSections.completedGames ? "" : "closed"}`}>⌄</span>
          </div>
          {openSections.completedGames ? (
            <div className="dcl-section-body">
              <div className="dcl-games-grid">
                {(recentCompletedGames.length ? recentCompletedGames : [null]).map((game, idx) => (
                  <div
                    className={`dcl-game-card dcl-completed-card ${game ? "dcl-completed-card-action" : ""}`}
                    key={`completed-${game?.id || idx}`}
                    onClick={() => game && openGame(game)}
                    onKeyDown={(event) => {
                      if (!game) return;
                      if (event.key === "Enter" || event.key === " ") {
                        event.preventDefault();
                        openGame(game);
                      }
                    }}
                    role={game ? "button" : undefined}
                    tabIndex={game ? 0 : undefined}
                  >
                    <div className="dcl-game-title">{game?.game_name?.name || "No completed games yet"}</div>
                    <div className="dcl-game-meta">
                      {game?.game_name?.category?.name || "Keep playing to unlock progress"}
                    </div>
                    {game ? (
                      <div className="dcl-completed-stats">
                        <span>Level {game?.level || game?.game_name?.level || 1}</span>
                        <span>Score {game?.score ?? "--"}</span>
                      </div>
                    ) : null}
                    <div className="dcl-completed-footer">
                      <span className="dcl-completed-badge">{game ? "Completed" : "No data"}</span>
                      {game?.created_at ? <small>{Moment(game.created_at).format("MMM D, YYYY")}</small> : null}
                    </div>
                    {game ? <div className="dcl-completed-open">Tap to open</div> : null}
                  </div>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      </div>
      </div>
    </>
  );
}

export default StudentDashboard;
